/**
 * Design tokens + component styles, scoped to the Shadow root.
 * Theme: "First Light" — Daybreak (light) / Nightflight (dark).
 * Canonical spec: /design.md (Stitch DESIGN.md format).
 */
export const styles = /* css */ `
:host {
  all: initial;
  /* ── Daybreak ── */
  --q-bg: #F7FAFC; --q-surface: #FFFFFF; --q-surface-sunken: #EDF3F8;
  --q-border: #D9E4EC; --q-border-strong: #C3D3E2;
  --q-text: #16222E; --q-text-muted: #5A6B7A;
  --q-accent: #0F76B8; --q-accent-hover: #0B5E94;
  --q-accent-soft: #E1F0FA; --q-accent-soft-border: #C9E0F3;
  --q-on-accent: #FFFFFF;
  --q-focus: #0C3D66; --q-focus-inner: #FFFFFF;
  --q-success: #1B7F5C; --q-warn: #A96204; --q-danger: #C0364C;
  /* Highlight overlay renders over the HOST page — theme-independent. */
  --q-highlight-ring: #0F76B8;
  --q-highlight-wash: rgba(15,118,184,.10);
  --q-highlight-halo: rgba(15,118,184,.14);
  --q-highlight-core: rgba(255,255,255,.55);
  --q-radius-sm: 10px; --q-radius: 16px; --q-radius-lg: 24px; --q-radius-full: 999px;
  --q-shadow-tint: 29,58,87;
  --q-shadow-1: 0 1px 2px rgba(var(--q-shadow-tint),.05);
  --q-shadow-2: 0 8px 28px rgba(var(--q-shadow-tint),.10);
  --q-shadow-lift: 0 14px 36px rgba(var(--q-shadow-tint),.14);
  --q-dur-1: 120ms; --q-dur-2: 180ms; --q-dur-3: 260ms;
  --q-ease: cubic-bezier(.22,.7,.25,1);
  --q-font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  --q-font-mono: ui-monospace, "SF Mono", "Cascadia Code", Menlo, Consolas, monospace;
  font-family: var(--q-font-sans);
  font-size: 14px; line-height: 21px; color: var(--q-text);
  -webkit-font-smoothing: antialiased;
}
@media (prefers-color-scheme: dark) {
  :host([data-theme="auto"]) {
    --q-bg:#0C1420; --q-surface:#141F2D; --q-surface-sunken:#070D15;
    --q-border:#24344A; --q-border-strong:#3A4C5E;
    --q-text:#E9F1F7; --q-text-muted:#93A7B8;
    --q-accent:#5BB4E8; --q-accent-hover:#7CC4EE;
    --q-accent-soft:#15293C; --q-accent-soft-border:#27415C;
    --q-on-accent:#0C1420;
    --q-focus:#A6CFF0; --q-focus-inner:#0C1420;
    --q-success:#46C08D; --q-warn:#E5AE4C; --q-danger:#F07E8B;
    --q-shadow-tint: 0,0,0;
    --q-shadow-1: 0 1px 2px rgba(var(--q-shadow-tint),.20);
    --q-shadow-2: 0 8px 28px rgba(var(--q-shadow-tint),.30);
    --q-shadow-lift: 0 14px 36px rgba(var(--q-shadow-tint),.35);
  }
}
:host([data-theme="dark"]) {
  --q-bg:#0C1420; --q-surface:#141F2D; --q-surface-sunken:#070D15;
  --q-border:#24344A; --q-border-strong:#3A4C5E;
  --q-text:#E9F1F7; --q-text-muted:#93A7B8;
  --q-accent:#5BB4E8; --q-accent-hover:#7CC4EE;
  --q-accent-soft:#15293C; --q-accent-soft-border:#27415C;
  --q-on-accent:#0C1420;
  --q-focus:#A6CFF0; --q-focus-inner:#0C1420;
  --q-success:#46C08D; --q-warn:#E5AE4C; --q-danger:#F07E8B;
  --q-shadow-tint: 0,0,0;
  --q-shadow-1: 0 1px 2px rgba(var(--q-shadow-tint),.20);
  --q-shadow-2: 0 8px 28px rgba(var(--q-shadow-tint),.30);
  --q-shadow-lift: 0 14px 36px rgba(var(--q-shadow-tint),.35);
}
* { box-sizing: border-box; }
button { font: inherit; cursor: pointer; border: none; background: none; color: inherit; }
:focus-visible { outline: 2px solid var(--q-focus); outline-offset: 2px; border-radius: 4px; }
/* Two-layer focus ring for accent-filled controls — a plain blue outline
   disappears on the now-blue fills. */
.launcher:focus-visible, .btn.primary:focus-visible,
.icon-btn.send:focus-visible, .icon-btn.mic.listening:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--q-focus-inner), 0 0 0 4px var(--q-focus);
}

.launcher {
  position: fixed; z-index: 2147483000; width: 52px; height: 52px; border-radius: var(--q-radius-full);
  background: var(--q-accent); color: var(--q-on-accent); box-shadow: var(--q-shadow-lift);
  display: grid; place-items: center;
  transition: transform var(--q-dur-1) var(--q-ease), background var(--q-dur-1) var(--q-ease), box-shadow var(--q-dur-1) var(--q-ease);
}
.launcher:hover { transform: translateY(-2px); background: var(--q-accent-hover); box-shadow: 0 18px 40px rgba(var(--q-shadow-tint),.18); }
.launcher svg { width: 24px; height: 24px; }
.corner-br { right: 20px; bottom: 20px; }
.corner-bl { left: 20px; bottom: 20px; }

.panel {
  position: fixed; z-index: 2147483000; width: 384px; max-width: calc(100vw - 32px);
  height: min(640px, 80vh); background: var(--q-bg); border: 1px solid var(--q-border);
  border-radius: var(--q-radius-lg); box-shadow: var(--q-shadow-2);
  display: flex; flex-direction: column; overflow: hidden;
  transform-origin: bottom right; animation: q-in var(--q-dur-2) var(--q-ease);
}
.panel.bl { transform-origin: bottom left; }
@keyframes q-in { from { opacity: 0; transform: scale(.97) translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes q-fade { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .panel { animation: q-fade var(--q-dur-2) linear; } }

.header {
  display: flex; align-items: center; gap: 8px; padding: 12px 14px;
  border-bottom: 1px solid var(--q-border); background: var(--q-surface);
  box-shadow: var(--q-shadow-1);
}
.header .mark { width: 22px; height: 22px; color: var(--q-accent); flex: 0 0 auto; }
.header .title { font-weight: 600; font-size: 15px; }
.header .status { margin-left: auto; font-size: 12px; color: var(--q-text-muted); display:flex; align-items:center; gap:6px; }
.dot { width: 7px; height: 7px; border-radius: var(--q-radius-full); background: var(--q-text-muted); }
.dot.working { background: var(--q-accent); animation: pulse 1.4s ease-in-out infinite; }
.dot.idle { background: var(--q-success); }
.dot.error { background: var(--q-danger); }
@keyframes pulse { 0%,100%{opacity:.4} 50%{opacity:1} }
.header .close { margin-left: 4px; color: var(--q-text-muted); border-radius: var(--q-radius-sm); padding: 4px; }
.header .close:hover { background: var(--q-surface-sunken); }

.transcript { flex: 1 1 auto; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 10px; }
.msg { max-width: 88%; padding: 8px 11px; border-radius: var(--q-radius); font-size: 14px; }
.msg.user { align-self: flex-end; background: var(--q-accent-soft); border: 1px solid var(--q-accent-soft-border); border-bottom-right-radius: 4px; }
.msg.assistant { align-self: flex-start; background: var(--q-surface); border: 1px solid var(--q-border); box-shadow: var(--q-shadow-1); border-bottom-left-radius: 4px; }
.tool-line { align-self: flex-start; font-size: 12px; color: var(--q-text-muted); display: flex; gap: 6px; }
.tool-line code { font-family: var(--q-font-mono); font-size: 11.5px; color: var(--q-accent); }
.empty { margin: auto; text-align: center; color: var(--q-text-muted); font-size: 13px; padding: 0 24px; }
.empty .big { font-size: 19px; font-weight: 600; letter-spacing: -0.015em; color: var(--q-text); margin-bottom: 6px; }

.confirm {
  align-self: stretch; background: var(--q-surface); border: 1px solid var(--q-border-strong);
  border-left: 3px solid var(--q-warn); border-radius: var(--q-radius); padding: 12px;
  box-shadow: var(--q-shadow-1);
}
.confirm.danger { border-left-color: var(--q-danger); }
.confirm .q { font-weight: 550; margin-bottom: 4px; }
.confirm .sub { color: var(--q-text-muted); font-size: 13px; margin-bottom: 10px; }
.confirm .row { display: flex; gap: 8px; justify-content: flex-end; }
.btn { padding: 7px 14px; border-radius: var(--q-radius-sm); font-size: 13px; font-weight: 550; letter-spacing: 0.01em; }
.btn.primary { background: var(--q-accent); color: var(--q-on-accent); }
.btn.primary:hover { background: var(--q-accent-hover); }
.btn.ghost { background: var(--q-surface-sunken); color: var(--q-text); }

.feed { border-top: 1px solid var(--q-border); background: var(--q-surface-sunken); box-shadow: inset 0 2px 4px rgba(var(--q-shadow-tint),.04); max-height: 132px; overflow-y: auto; }
.feed summary { list-style: none; cursor: pointer; padding: 8px 14px; font-size: 12px; color: var(--q-text-muted); user-select: none; }
.feed summary::-webkit-details-marker { display: none; }
.feed .item { padding: 4px 14px; font-size: 12px; display: flex; gap: 8px; align-items: baseline; }
.feed .item code { font-family: var(--q-font-mono); font-size: 11px; }
.feed .item.err { color: var(--q-danger); }
.feed .item .sd { width: 6px; height: 6px; border-radius: var(--q-radius-full); margin-top: 5px; flex: 0 0 auto; background: var(--q-success); }
.feed .item.err .sd { background: var(--q-danger); }

.composer { border-top: 1px solid var(--q-border); background: var(--q-surface); padding: 10px; display: flex; gap: 8px; align-items: flex-end; }
.composer textarea {
  flex: 1 1 auto; resize: none; border: 1px solid var(--q-border-strong); border-radius: var(--q-radius);
  padding: 9px 11px; font: inherit; background: var(--q-bg); color: var(--q-text); max-height: 120px; min-height: 40px;
}
.composer textarea:focus { outline: 2px solid var(--q-focus); outline-offset: 1px; box-shadow: 0 0 0 1px var(--q-focus-inner); }
.icon-btn { width: 40px; height: 40px; border-radius: var(--q-radius-full); display: grid; place-items: center; flex: 0 0 auto; border: 1px solid var(--q-border); background: var(--q-surface); }
.icon-btn svg { width: 18px; height: 18px; }
.icon-btn.mic.listening { background: var(--q-accent); color: var(--q-on-accent); border-color: var(--q-accent); animation: pulse 1.4s ease-in-out infinite; box-shadow: 0 0 0 6px var(--q-accent-soft); }
.icon-btn.send { background: var(--q-accent); color: var(--q-on-accent); border-color: var(--q-accent); }
.icon-btn.send:hover { background: var(--q-accent-hover); }
.icon-btn.stop { background: var(--q-surface); color: var(--q-danger); border-color: var(--q-danger); }

/* Nib-stroke highlight ring — overlays the HOST page (theme-independent):
   accent ring + white inner keyline + soft halo survives light and dark hosts. */
.q-highlight {
  position: fixed; z-index: 2147482000; pointer-events: none;
  border: 2px solid var(--q-highlight-ring, #0F76B8);
  background: var(--q-highlight-wash, rgba(15,118,184,.10)); border-radius: 8px;
  box-shadow: inset 0 0 0 1px var(--q-highlight-core, rgba(255,255,255,.55)), 0 0 0 4px var(--q-highlight-halo, rgba(15,118,184,.14));
  transition: opacity var(--q-dur-3) ease;
}
@media (prefers-reduced-motion: reduce) { .icon-btn.mic.listening, .dot.working { animation: none; } .icon-btn.mic.listening { box-shadow: none; } }
`;
//# sourceMappingURL=styles.js.map